import { useSelector } from "react-redux";
import {
  hasRole,
  hasPermission,
  isSuperAdmin,
  isAdmin,
  canManageAdmins,
} from "../utils/roleUtils";

/**
 * Component to conditionally render content based on user role
 * @param {Object} props - Component props
 * @param {string} props.role - Required role
 * @param {string} props.permission - Required permission
 * @param {boolean} props.requireSuperAdmin - Require super admin role
 * @param {boolean} props.requireAdmin - Require at least admin role
 * @param {boolean} props.requireAdminManagement - Require admin management capability
 * @param {React.ReactNode} props.children - Content to render if authorized
 * @param {React.ReactNode} props.fallback - Content to render if not authorized
 * @returns {React.ReactElement}
 */
const RoleGuard = ({
  role,
  permission,
  requireSuperAdmin,
  requireAdmin,
  requireAdminManagement,
  children,
  fallback = null,
}) => {
  const user = useSelector((state) => state.auth.user);

  let isAuthorized = false;

  // Check specific role
  if (role) {
    isAuthorized = hasRole(user, role);
  }
  // Check specific permission
  else if (permission) {
    isAuthorized = hasPermission(user, permission);
  }
  // Check for super admin
  else if (requireSuperAdmin) {
    isAuthorized = isSuperAdmin(user);
  }
  // Check for at least admin
  else if (requireAdmin) {
    isAuthorized = isAdmin(user);
  }
  // Check for admin management capability
  else if (requireAdminManagement) {
    isAuthorized = canManageAdmins(user);
  }
  // Default to true if no specific requirement
  else {
    isAuthorized = true;
  }

  return isAuthorized ? children : fallback;
};

export default RoleGuard;
